import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';

import { companyApi } from '@/api';
import {
  Badge,
  Button,
  Card,
  CardHeader,
  EmptyState,
  ErrorState,
  Icon,
  IconButton,
  Input,
  LoadingState,
  Textarea,
  useToast,
} from '@/components/ui';
import { RecordHeader } from '@/features/admin/RecordHeader';
import { ReportButton } from '@/features/reports/ReportButton';
import { VerifiedBadge } from '@/features/verification/VerifiedBadge';
import { APPLICATION_TONES } from '@/features/applications/status';
import { MessageCandidateButton } from '@/features/messages/MessageCandidateButton';
import { errorMessage } from '@/hooks/useApiError';
import { paths } from '@/routes/paths';
import { formatDate, formatMonthYear, humanizeWorkMode, relativeTime } from '@/utils/format';

/** One entry in a list on this page -- a job, a degree, an application, a note. */
const ENTRY = 'border-line rounded-input flex gap-3 border p-3';
const ENTRY_ICON =
  'bg-brand-50 text-brand-600 ring-brand-100 grid size-10 shrink-0 place-items-center rounded-xl ring-1 ring-inset';
const ENTRY_ICON_SM =
  'bg-brand-50 text-brand-600 ring-brand-100 grid size-8 shrink-0 place-items-center rounded-lg ring-1 ring-inset';

function dateRange(from, to, isCurrent) {
  return `${formatMonthYear(from)} — ${isCurrent ? 'present' : formatMonthYear(to)}`;
}

export function CandidateDetailPage() {
  const { id } = useParams();
  const queryClient = useQueryClient();
  const toast = useToast();
  const [tagDraft, setTagDraft] = useState('');
  const [noteDraft, setNoteDraft] = useState('');

  const { data, isPending, isError, error, refetch } = useQuery({
    queryKey: ['candidate', id],
    queryFn: () => companyApi.getCandidate(id),
  });

  function invalidate() {
    void queryClient.invalidateQueries({ queryKey: ['candidate', id] });
    void queryClient.invalidateQueries({ queryKey: ['talent-pool'] });
    void queryClient.invalidateQueries({ queryKey: ['candidate-tags'] });
    void queryClient.invalidateQueries({ queryKey: ['candidate-search'] });
  }

  const save = useMutation({
    mutationFn: () => companyApi.addToTalentPool({ student_id: id }),
    onSuccess: () => {
      toast.success('Saved to your candidates');
      invalidate();
    },
    onError: (saveError) => toast.error(errorMessage(saveError, 'Could not save the candidate')),
  });

  const remove = useMutation({
    mutationFn: () => companyApi.removeFromTalentPool(id),
    onSuccess: () => {
      toast.success('Removed from saved candidates');
      invalidate();
    },
    onError: (removeError) => toast.error(errorMessage(removeError, 'Could not remove')),
  });

  const setTags = useMutation({
    mutationFn: (tags) => companyApi.setCandidateTags(id, tags),
    onSuccess: () => {
      setTagDraft('');
      invalidate();
    },
    onError: (tagError) => toast.error(errorMessage(tagError, 'Could not update the tags')),
  });

  const addNote = useMutation({
    mutationFn: (body) => companyApi.addCandidateNote(id, body),
    onSuccess: () => {
      setNoteDraft('');
      invalidate();
    },
    onError: (noteError) => toast.error(errorMessage(noteError, 'Could not add the note')),
  });

  const deleteNote = useMutation({
    mutationFn: (noteId) => companyApi.deleteCandidateNote(id, noteId),
    onSuccess: invalidate,
    onError: (noteError) => toast.error(errorMessage(noteError, 'Could not delete the note')),
  });

  if (isPending) return <LoadingState label="Loading the candidate…" />;
  if (isError) {
    return <ErrorState message={errorMessage(error)} onRetry={() => void refetch()} />;
  }

  const tags = data.pool?.tags ?? [];

  return (
    <div>
      <RecordHeader
        eyebrow="Candidate"
        title={data.full_name}
        subtitle={data.headline ?? 'No headline yet.'}
        actions={
          <>
            <ReportButton targetType="STUDENT" targetId={data.id} targetName={data.full_name} />
            <MessageCandidateButton candidate={data} size="md" />
            {data.pool ? (
              <Button
                variant="secondary"
                isLoading={remove.isPending}
                onClick={() => remove.mutate()}
              >
                Remove from saved
              </Button>
            ) : (
              <Button isLoading={save.isPending} onClick={() => save.mutate()}>
                <Icon name="bookmark" size={16} />
                Save candidate
              </Button>
            )}
          </>
        }
      />

      {/* Contact details are shown to searching companies since 2026-10-27;
          only the CV still waits for an application. The old notice said
          the email was hidden, so it is kept here switched off (import
          Alert again to restore it).
      {!data.is_unlocked && (
        <Alert tone="info" className="mb-6" title="Contact details are hidden">
          You can see this profile because {data.full_name.split(' ')[0]} is open to opportunities.
          Their email address becomes visible once they apply to one of your internships.
        </Alert>
      )} */}

      <div className="grid items-start gap-6 lg:grid-cols-[1fr_340px]">
        <div className="min-w-0 space-y-6">
          <Card>
            <CardHeader title="About" />
            <dl className="grid gap-3 sm:grid-cols-2">
              <Fact label="University" value={data.university} />
              <Fact label="Degree" value={data.degree} />
              <Fact label="Field of study" value={data.field_of_study} />
              <Fact label="Graduating" value={data.graduation_year} />
              <Fact label="Location" value={data.location} />
              <Fact
                label="Prefers"
                value={data.preferred_work_mode ? humanizeWorkMode(data.preferred_work_mode) : null}
              />
              <Fact
                label="Available from"
                value={data.available_from ? formatDate(data.available_from) : null}
              />
              <Fact label="Profile completion" value={`${data.profile_completion}%`} />
            </dl>
            {data.bio && (
              <p className="text-ink-muted border-line mt-4 border-t pt-4 text-sm leading-6 whitespace-pre-line">
                {data.bio}
              </p>
            )}
          </Card>

          {data.skills.length > 0 && (
            <Card>
              <CardHeader title="Skills" />
              <div className="flex flex-wrap gap-1.5">
                {data.skills.map((skill) => (
                  <Badge key={skill.id} tone="neutral">
                    {skill.name}
                    <span className="ml-1 opacity-70">{skill.proficiency.toLowerCase()}</span>
                  </Badge>
                ))}
              </div>
            </Card>
          )}

          {data.experience.length > 0 && (
            <Card>
              <CardHeader title="Experience" />
              <ol className="space-y-3">
                {data.experience.map((row, index) => (
                  <li key={`${row.company_name}-${index}`} className={ENTRY}>
                    <span aria-hidden="true" className={ENTRY_ICON}>
                      <Icon name="company" size={18} />
                    </span>
                    <div className="min-w-0">
                      <p className="text-ink text-[15px] leading-snug font-semibold">{row.title}</p>
                      <p className="text-ink-muted mt-0.5 text-sm">
                        {row.company_name}
                        {row.location ? ` · ${row.location}` : ''}
                      </p>
                      <p className="text-ink-muted mt-2 inline-flex items-center gap-1.5 text-xs">
                        <Icon name="calendar" size={14} />
                        {dateRange(row.start_date, row.end_date, row.is_current)}
                      </p>
                      {row.description && (
                        <p className="text-ink-muted mt-2 text-sm leading-6">{row.description}</p>
                      )}
                    </div>
                  </li>
                ))}
              </ol>
            </Card>
          )}

          {data.education.length > 0 && (
            <Card>
              <CardHeader title="Education" />
              <ol className="space-y-3">
                {data.education.map((row, index) => (
                  <li key={`${row.institution_name}-${index}`} className={ENTRY}>
                    <span aria-hidden="true" className={ENTRY_ICON}>
                      <Icon name="university" size={18} />
                    </span>
                    <div className="min-w-0">
                      <p className="text-ink text-[15px] leading-snug font-semibold">
                        {row.degree}
                      </p>
                      <p className="text-ink-muted mt-0.5 text-sm">
                        {row.institution_name}
                        {row.field_of_study ? ` · ${row.field_of_study}` : ''}
                      </p>
                      <p className="text-ink-muted mt-2 inline-flex items-center gap-1.5 text-xs">
                        <Icon name="calendar" size={14} />
                        {dateRange(row.start_date, row.end_date, row.is_current)}
                      </p>
                    </div>
                  </li>
                ))}
              </ol>
            </Card>
          )}

          {data.certifications?.length > 0 && (
            <Card>
              <CardHeader
                title="Certifications"
                description="Verified means our team has checked the certificate itself."
              />
              <ol className="space-y-3">
                {data.certifications.map((row, index) => (
                  <li key={`${row.name}-${index}`} className={ENTRY}>
                    <span aria-hidden="true" className={ENTRY_ICON}>
                      <Icon name={row.verified ? 'shieldCheck' : 'idBadge'} size={18} />
                    </span>
                    <div className="min-w-0">
                      <p className="text-ink flex flex-wrap items-center gap-2 text-[15px] leading-snug font-semibold">
                        {row.name}
                        {row.verified && <VerifiedBadge />}
                      </p>
                      {row.issuer && <p className="text-ink-muted mt-0.5 text-sm">{row.issuer}</p>}
                      {(row.issue_date || row.expiry_date) && (
                        <p className="text-ink-muted mt-2 inline-flex items-center gap-1.5 text-xs">
                          <Icon name="calendar" size={14} />
                          {[
                            row.issue_date && `Issued ${formatDate(row.issue_date)}`,
                            row.expiry_date && `expires ${formatDate(row.expiry_date)}`,
                          ]
                            .filter(Boolean)
                            .join(' · ')}
                        </p>
                      )}
                    </div>
                  </li>
                ))}
              </ol>
            </Card>
          )}
        </div>

        <div className="space-y-6">
          {data.contact && (
            <Card>
              <CardHeader
                title="Contact"
                description="Reach out by email or phone, or send a message here."
              />
              <ul className="space-y-2 text-sm">
                <li>
                  <a
                    href={`mailto:${data.contact.email}`}
                    className="text-ink hover:text-brand-700 inline-flex items-center gap-2 break-all hover:underline"
                  >
                    <Icon name="mail" size={15} className="text-ink-muted shrink-0" />
                    {data.contact.email}
                  </a>
                </li>
                <li>
                  {data.contact.phone ? (
                    <a
                      href={`tel:${data.contact.phone}`}
                      className="text-ink hover:text-brand-700 inline-flex items-center gap-2 hover:underline"
                    >
                      <Icon name="phone" size={15} className="text-ink-muted shrink-0" />
                      {data.contact.phone}
                    </a>
                  ) : (
                    <span className="text-ink-muted inline-flex items-center gap-2">
                      <Icon name="phone" size={15} className="shrink-0" />
                      No phone number added
                    </span>
                  )}
                </li>
              </ul>
              <div className="mt-3 flex flex-col gap-1 text-sm">
                {['linkedin_url', 'github_url', 'portfolio_url']
                  .filter((key) => data.contact[key])
                  .map((key) => (
                    <a
                      key={key}
                      href={data.contact[key]}
                      target="_blank"
                      rel="noreferrer noopener"
                      className="text-brand-700 break-all hover:underline"
                    >
                      {data.contact[key]}
                    </a>
                  ))}
              </div>
            </Card>
          )}

          <Card>
            <CardHeader title="Applications to you" />
            {data.applications.length === 0 ? (
              <EmptyState
                compact
                icon={<Icon name="application" size={16} />}
                title="They have not applied to any of your internships."
              />
            ) : (
              <ol className="space-y-3">
                {data.applications.map((application) => (
                  <li key={application.id} className={ENTRY}>
                    <span aria-hidden="true" className={ENTRY_ICON_SM}>
                      <Icon name="application" size={16} />
                    </span>
                    <div className="min-w-0">
                      <Link
                        to={paths.company.applicationDetail(application.id)}
                        className="text-ink hover:text-brand-700 text-sm font-semibold transition"
                      >
                        {application.internship_title}
                      </Link>
                      <div className="mt-1.5 flex flex-wrap items-center gap-2">
                        <Badge dot tone={APPLICATION_TONES[application.status] ?? 'neutral'}>
                          {application.status.replace('_', ' ')}
                        </Badge>
                        <span className="text-ink-muted text-xs">
                          {formatDate(application.created_at)}
                        </span>
                      </div>
                    </div>
                  </li>
                ))}
              </ol>
            )}
          </Card>

          {data.pool && (
            <Card>
              <CardHeader title="Tags" />
              <div className="flex flex-wrap gap-1.5">
                {tags.length === 0 && <p className="text-ink-muted text-sm">No tags yet.</p>}
                {tags.map((tag) => (
                  <button
                    key={tag}
                    type="button"
                    onClick={() => setTags.mutate(tags.filter((item) => item !== tag))}
                    className="bg-brand-50 text-brand-700 ring-brand-100 hover:bg-danger-50 hover:text-danger-700 hover:ring-danger-100 inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-medium ring-1 transition ring-inset"
                    aria-label={`Remove tag ${tag}`}
                  >
                    #{tag}
                    <Icon name="close" size={12} />
                  </button>
                ))}
              </div>
              <form
                className="mt-3 flex gap-2"
                onSubmit={(event) => {
                  event.preventDefault();
                  const label = tagDraft.trim().toLowerCase();
                  if (label) setTags.mutate([...tags, label]);
                }}
              >
                {/* Input's className lands on the control, so the wrapper
                    carries the flex sizing. */}
                <div className="flex-1">
                  <Input
                    placeholder="backend"
                    value={tagDraft}
                    onChange={(event) => setTagDraft(event.target.value)}
                  />
                </div>
                <Button type="submit" disabled={!tagDraft.trim()} isLoading={setTags.isPending}>
                  Add
                </Button>
              </form>
              <p className="text-ink-muted mt-2 text-xs">
                Saved by {data.pool.added_by ?? 'a teammate'} on {formatDate(data.pool.added_at)}.
              </p>
            </Card>
          )}

          <Card>
            <CardHeader
              title="Internal notes"
              description="Only your team sees these. They are kept if you remove the candidate from saved candidates."
            />
            <form
              onSubmit={(event) => {
                event.preventDefault();
                if (noteDraft.trim()) addNote.mutate(noteDraft.trim());
              }}
            >
              <Textarea
                rows={3}
                placeholder="Strong on data modelling; follow up in September."
                value={noteDraft}
                onChange={(event) => setNoteDraft(event.target.value)}
              />
              <div className="mt-2 flex justify-end">
                <Button
                  type="submit"
                  size="sm"
                  isLoading={addNote.isPending}
                  disabled={!noteDraft.trim()}
                >
                  Add note
                </Button>
              </div>
            </form>

            {data.notes.length === 0 ? (
              <div className="mt-4">
                <EmptyState
                  compact
                  title="No notes yet"
                  description="Write down what you thought."
                />
              </div>
            ) : (
              <ol className="mt-4 space-y-3">
                {data.notes.map((note) => (
                  <li key={note.id} className={ENTRY}>
                    <div className="min-w-0 flex-1">
                      <p className="text-ink text-sm whitespace-pre-line">{note.body}</p>
                      <p className="text-ink-muted mt-1.5 text-xs">
                        {note.author_name ?? 'A former teammate'} · {relativeTime(note.created_at)}
                      </p>
                    </div>
                    <IconButton
                      icon="trash"
                      label="Delete note"
                      variant="ghostDanger"
                      className="shrink-0"
                      onClick={() => deleteNote.mutate(note.id)}
                    />
                  </li>
                ))}
              </ol>
            )}
          </Card>
        </div>
      </div>
    </div>
  );
}

function Fact({ label, value }) {
  if (!value) return null;
  return (
    <div>
      <dt className="text-ink-muted text-xs">{label}</dt>
      <dd className="text-ink text-sm">{value}</dd>
    </div>
  );
}
